import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Divider,
  Alert, CircularProgress, Radio, RadioGroup, FormControlLabel,
  Collapse, InputAdornment, Skeleton, Container, Stepper, Step,
  StepLabel, Breadcrumbs, Link, useTheme, useMediaQuery, Card,
  CardContent, Avatar, Chip
} from '@mui/material';
import { toast } from 'react-toastify';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SecurityIcon from '@mui/icons-material/Security';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import EInvoiceSelector from '../components/EInvoiceSelector';
import { trackTikTokInitiateCheckout } from '../components/TikTokPixel';
import TaiwanAddressSelector from '../components/TaiwanAddressSelector';

const steps = ['確認商品', '填寫資料', '選擇付款'];

const Checkout = () => {
  const { cart, sessionId, clearCart } = useCart();
  const { setAuthFromCheckout, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(true); // 不再需要手機驗證，直接允許下單
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const hasTrackedCheckout = useRef(false);
  const hasTrackedShipping = useRef(false);
  const hasTrackedPayment = useRef(false);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [shippingConfig, setShippingConfig] = useState({ base_fee: 60, free_shipping_threshold: 0 });
  const [emapLoading, setEmapLoading] = useState(false);

  // 門市選擇對話框狀態

  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    shipping_method: 'cvs', // cvs: 超商取貨, home: 宅配
    store_type: '7-11',
    store_id: '',
    store_name: '',
    store_address: '',
    // 宅配地址
    home_address: '',
    home_city: '',
    home_district: '',
    home_zipcode: '',
    remark: '',
    payment_method: ''
  });
  const [invoiceData, setInvoiceData] = useState({
    type: 'carrier',
    carrierType: 'EJ0113',
    carrierId: null,
    buyerId: null,
    companyName: null,
    loveCode: null,
    donate: false
  });

  const subtotal = cart?.subtotal || 0;
  // 依配送方式取運費（與 server/models/order.model.js 同一套公式，避免顧客看到的
  // 金額與實際請款不符）：超商 cvs_fee、宅配 home_fee，未設定則回退 base_fee。
  const toInt = (v, d) => (Number.isFinite(parseInt(v, 10)) ? parseInt(v, 10) : d);
  const isHomeDelivery = form.shipping_method === 'home';
  const fallbackFee = toInt(shippingConfig.base_fee, 60);
  const fallbackFree = toInt(shippingConfig.free_shipping_threshold, 0);
  const currentShippingFee = isHomeDelivery
    ? toInt(shippingConfig.home_fee, fallbackFee)
    : toInt(shippingConfig.cvs_fee, fallbackFee);
  const currentFreeThreshold = isHomeDelivery
    ? toInt(shippingConfig.home_free_threshold, fallbackFree)
    : toInt(shippingConfig.cvs_free_threshold, fallbackFree);
  const shippingFee = currentFreeThreshold > 0 && subtotal >= currentFreeThreshold
    ? 0
    : currentShippingFee;
  const total = subtotal + shippingFee;

  // 計算總共省了多少 (原價 - 售價)
  const calculateTotalSavings = () => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => {
      const originalPrice = item.original_price || item.sale_price;
      const salePrice = item.display_price || item.sale_price;
      return sum + (originalPrice - salePrice) * item.quantity;
    }, 0);
  };
  const totalSavings = calculateTotalSavings();

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [paymentRes, shippingRes] = await Promise.all([
          api.get('/settings/payment-methods/active'),
          api.get('/settings/shipping')
        ]);

        if (paymentRes.data.success && paymentRes.data.payment_methods.length > 0) {
          setPaymentMethods(paymentRes.data.payment_methods);
          setForm(prev => ({
            ...prev,
            payment_method: paymentRes.data.payment_methods[0].code
          }));
        }

        if (shippingRes.data.success) {
          // 運費依配送方式而異（超商 cvs_fee / 宅配 home_fee），故整包帶入，
          // 由下方 shippingFee 依當前選擇的 shipping_method 計算，與伺服器端公式一致。
          setShippingConfig(shippingRes.data.config || {});
        }
      } catch (error) {
        console.error('載入設定失敗:', error);
        setPaymentMethods([
          { code: 'cod', name: '取貨付款', description: '到超商取貨時付現金', icon: '💵' },
          { code: 'credit', name: '信用卡', description: 'SmilePay 信用卡線上付款', icon: '💳' },
          { code: 'smilepay', name: '超商代碼繳費', description: '取得代碼後至超商繳費', icon: '🏪' }
        ]);
        setForm(prev => ({ ...prev, payment_method: 'cod' }));
      } finally {
        setLoadingPayments(false);
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 購物車驗證與追蹤事件 (只執行一次)
  useEffect(() => {
    if (!cart || cart.items.length === 0) {
      navigate('/cart');
      return;
    }

    // 防止重複追蹤 (使用 ref，解決 React 18 StrictMode 雙重執行問題)
    if (hasTrackedCheckout.current) {
      return;
    }
    hasTrackedCheckout.current = true;

    // TikTok Pixel InitiateCheckout 事件
    trackTikTokInitiateCheckout(cart);

    // GTM/GA4 begin_checkout 事件
    if (window.dataLayer) {
      window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce data
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'TWD',
          value: cart.subtotal,
          items: cart.items.map(item => ({
            item_id: item.sku,
            item_name: item.name,
            price: item.sale_price,
            quantity: item.quantity
          }))
        }
      });
    }

    // Facebook Pixel InitiateCheckout 事件
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_type: 'product',
        content_ids: cart.items.map(item => item.sku),
        value: cart.subtotal,
        currency: 'TWD',
        num_items: cart.items.length
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  // 監聽 SmilePay E-MAP 門市選擇回傳
  useEffect(() => {
    const handleStoreMessage = (event) => {
      // 驗證訊息類型
      if (event.data && event.data.type === 'CVS_STORE_SELECTED') {
        const store = event.data.store;
        if (store && store.name) {
          // 自動填入門市資訊
          setForm(prev => ({
            ...prev,
            store_id: store.id || '',
            store_name: store.name,
            store_address: store.address || ''
          }));
          setEmapLoading(false);
          toast.success(`已選擇門市：${store.name}`);

          // GA4 add_shipping_info 事件
          if (!hasTrackedShipping.current && cart && window.dataLayer) {
            hasTrackedShipping.current = true;
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
              event: 'add_shipping_info',
              ecommerce: {
                currency: 'TWD',
                value: cart.subtotal,
                shipping_tier: 'cvs',
                items: cart.items.map(item => ({
                  item_id: item.sku,
                  item_name: item.name,
                  price: item.sale_price,
                  quantity: item.quantity
                }))
              }
            });
          }
        }
      }
    };

    window.addEventListener('message', handleStoreMessage);
    return () => window.removeEventListener('message', handleStoreMessage);
  }, []);

  // 用戶資料填充
  useEffect(() => {
    // 如果已登入，使用登入資料自動填入
    if (isAuthenticated && user) {
      setForm(prev => ({
        ...prev,
        customer_phone: user.phone || prev.customer_phone,
        customer_name: user.name || prev.customer_name,
        customer_email: user.email || prev.customer_email
      }));
    } else {
      // 否則檢查 localStorage 回填上次資料
      const savedPhone = localStorage.getItem('verified_phone');
      const savedName = localStorage.getItem('customer_name');
      if (savedPhone) {
        setForm(prev => ({
          ...prev,
          customer_phone: savedPhone,
          customer_name: savedName || ''
        }));
      }
    }
  }, [isAuthenticated, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    if (name === 'customer_phone') {
      setShowVerification(false);
    }
  };

  const sendVerificationCode = async () => {
    if (!/^09\d{8}$/.test(form.customer_phone)) {
      toast.error('請輸入正確的手機號碼 (09xxxxxxxx)');
      return;
    }

    setVerifying(true);
    try {
      const response = await api.post('/auth/send-code', {
        phone: form.customer_phone
      });

      if (response.data.success) {
        setShowVerification(true);
        setCountdown(60);
        toast.success('驗證碼已發送至您的手機');

        if (response.data.code) {
          toast.info(`測試驗證碼: ${response.data.code}`, { autoClose: 10000 });
        }
      }
    } catch (error) {
      toast.error('發送驗證碼失敗，請稍後再試');
    } finally {
      setVerifying(false);
    }
  };

  const verifyPhone = async () => {
    if (!verificationCode || verificationCode.length !== 4) {
      toast.error('請輸入4位數驗證碼');
      return;
    }

    setVerifying(true);
    try {
      const response = await api.post('/auth/verify-code', {
        phone: form.customer_phone,
        code: verificationCode
      });

      if (response.data.success) {
        setPhoneVerified(true);
        localStorage.setItem('verified_phone', form.customer_phone);
        toast.success('手機驗證成功！您已成為會員');

        // 設定登入狀態
        if (response.data.token && response.data.user) {
          setAuthFromCheckout(response.data.token, response.data.user);
        }

        if (response.data.customer || response.data.user) {
          const name = response.data.customer?.name || response.data.user?.name;
          if (name) {
            setForm(prev => ({
              ...prev,
              customer_name: name
            }));
            localStorage.setItem('customer_name', name);
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.error || '驗證碼錯誤，請重新輸入');
    } finally {
      setVerifying(false);
    }
  };

  // 開啟 SmilePay 電子地圖
  const handleOpenStoreMap = async () => {
    try {
      setEmapLoading(true);

      // 呼叫 SmilePay 電子地圖 API
      const response = await api.get('/stores/smilepay-emap', {
        params: { store_type: form.store_type }
      });

      if (response.data.success && response.data.map_url) {
        console.log('SmilePay 電子地圖:', response.data.map_url);

        // 開啟電子地圖彈出視窗
        const mapWindow = window.open(
          response.data.map_url,
          'StoreMap',
          'width=900,height=700,scrollbars=yes,resizable=yes'
        );

        if (!mapWindow) {
          toast.error('無法開啟電子地圖，請允許彈出視窗後重試');
          setEmapLoading(false);
        } else {
          // 監控視窗關閉
          const checkClosed = setInterval(() => {
            if (mapWindow.closed) {
              clearInterval(checkClosed);
              setEmapLoading(false);
            }
          }, 1000);
        }
      } else {
        toast.error('無法取得電子地圖，請稍後再試');
        setEmapLoading(false);
      }
    } catch (error) {
      console.error('開啟電子地圖失敗:', error);
      toast.error('開啟電子地圖失敗，請稍後再試');
      setEmapLoading(false);
    }
  };


  const scrollToField = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSubmit = async () => {
    // 驗證手機號碼格式 (09 開頭 10 碼)
    if (!form.customer_phone.trim() || !/^09\d{8}$/.test(form.customer_phone.trim())) {
      toast.error('請填寫正確的手機號碼');
      scrollToField('section-contact');
      return;
    }
    if (!form.customer_name.trim()) {
      toast.error('請填寫收件人姓名');
      scrollToField('field-customer-name');
      return;
    }

    // 根據配送方式驗證
    if (form.shipping_method === 'cvs') {
      if (!form.store_name || !form.store_address) {
        toast.error('請選擇取貨門市');
        scrollToField('section-shipping');
        return;
      }
    } else if (form.shipping_method === 'home') {
      if (!form.home_city.trim()) {
        toast.error('請填寫縣市');
        scrollToField('section-shipping');
        return;
      }
      if (!form.home_address.trim()) {
        toast.error('請填寫配送地址');
        scrollToField('section-shipping');
        return;
      }
    }

    // GA4 add_shipping_info 事件 (宅配模式補發)
    if (!hasTrackedShipping.current && cart && window.dataLayer) {
      hasTrackedShipping.current = true;
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_shipping_info',
        ecommerce: {
          currency: 'TWD',
          value: cart.subtotal,
          shipping_tier: form.shipping_method === 'home' ? 'home_delivery' : 'cvs',
          items: cart.items.map(item => ({
            item_id: item.sku,
            item_name: item.name,
            price: item.sale_price,
            quantity: item.quantity
          }))
        }
      });
    }

    setLoading(true);
    try {
      localStorage.setItem('customer_name', form.customer_name);
      localStorage.setItem('verified_phone', form.customer_phone);

      const orderResponse = await api.post('/orders', {
        ...form,
        shipping_fee: shippingFee,
        invoice_data: invoiceData
      }, {
        headers: { 'x-session-id': sessionId }
      });

      if (orderResponse.data.success) {
        const { order_no } = orderResponse.data.order;

        switch (form.payment_method) {
          case 'cod':
            clearCart();
            toast.success('訂單建立成功！取貨時付款');
            navigate(`/checkout/complete/${order_no}`);
            break;

          case 'linepay':
            const linepayResponse = await api.post('/linepay/request', {
              order_no,
              amount: total
            });
            if (linepayResponse.data.success) {
              window.location.href = linepayResponse.data.paymentUrl;
              return;
            }
            break;

          case 'credit':
          case 'credit_card':
            // 信用卡付款 - 取得表單資料並提交到 SmilePay
            const creditResponse = await api.post('/smilepay/payment/credit-card', {
              order_no
            });
            if (creditResponse.data.success) {
              // 建立隱藏表單並提交
              const paymentForm = document.createElement('form');
              paymentForm.method = 'POST';
              paymentForm.action = creditResponse.data.formAction;
              paymentForm.style.display = 'none';

              Object.entries(creditResponse.data.formData).forEach(([key, value]) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = value;
                paymentForm.appendChild(input);
              });

              document.body.appendChild(paymentForm);
              clearCart();
              paymentForm.submit();
              return;
            } else {
              throw new Error(creditResponse.data.error || '信用卡付款建立失敗');
            }

          case 'atm':
            // ATM 虛擬帳號
            const atmResponse = await api.post('/smilepay/payment/atm', {
              order_no
            });
            if (atmResponse.data.success) {
              clearCart();
              toast.success('虛擬帳號建立成功！');
              navigate(`/checkout/complete/${order_no}`, {
                state: {
                  paymentInfo: {
                    type: 'atm',
                    bankName: atmResponse.data.bankName,
                    bankNo: atmResponse.data.atmBankNo,
                    accountNo: atmResponse.data.atmNo,
                    deadline: atmResponse.data.payEndDate
                  }
                }
              });
              return;
            } else {
              throw new Error(atmResponse.data.error || 'ATM 取號失敗');
            }

          case 'cvs':
          case 'cvs_barcode':
          case 'smilepay':
            // 超商代碼繳費
            const cvsResponse = await api.post('/smilepay/payment/cvs', {
              order_no
            });
            if (cvsResponse.data.success) {
              clearCart();
              toast.success('繳費條碼建立成功！');
              navigate(`/checkout/complete/${order_no}`, {
                state: {
                  paymentInfo: {
                    type: 'cvs_barcode',
                    barcode1: cvsResponse.data.barcode1,
                    barcode2: cvsResponse.data.barcode2,
                    barcode3: cvsResponse.data.barcode3,
                    deadline: cvsResponse.data.payEndDate
                  }
                }
              });
              return;
            } else {
              throw new Error(cvsResponse.data.error || '超商代碼建立失敗');
            }

          case 'c2c':
            // 超商取貨付款
            const c2cResponse = await api.post('/smilepay/payment/c2c', {
              order_no,
              store_type: form.store_type,
              store_id: form.store_id,
              store_name: form.store_name,
              store_address: form.store_address
            });
            if (c2cResponse.data.success) {
              clearCart();
              toast.success('取貨付款單建立成功！');
              navigate(`/checkout/complete/${order_no}`);
              return;
            } else {
              throw new Error(c2cResponse.data.error || '取貨付款單建立失敗');
            }

          default:
            clearCart();
            navigate(`/checkout/complete/${order_no}`);
        }
      }
    } catch (error) {
      console.error('建立訂單失敗:', error);
      toast.error(error.response?.data?.error || '訂單建立失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const getSubmitButtonText = () => {
    switch (form.payment_method) {
      case 'cod':
        return `確認訂單 $${total.toLocaleString()}`;
      case 'linepay':
        return `使用 LINE Pay 付款 $${total.toLocaleString()}`;
      case 'credit':
        return `使用信用卡付款 $${total.toLocaleString()}`;
      case 'smilepay':
        return `取得繳費代碼 $${total.toLocaleString()}`;
      case 'atm':
        return `取得轉帳帳號 $${total.toLocaleString()}`;
      default:
        return `送出訂單 $${total.toLocaleString()}`;
    }
  };

  if (!cart || cart.items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* 麵包屑導航 */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            首頁
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/cart')}
            sx={{ cursor: 'pointer' }}
          >
            購物車
          </Link>
          <Typography color="text.primary">結帳</Typography>
        </Breadcrumbs>

        {/* 結帳步驟 */}
        {!isMobile && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label} completed={index < activeStep}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        )}

        <Grid container spacing={3}>
          {/* 左側 - 主要表單 */}
          <Grid item xs={12} md={8}>
            {/* 購買商品 */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#1769ff', width: 32, height: 32 }}>
                  <ShoppingCartIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  購買商品
                </Typography>
                <Chip label={`${cart.items.length} 件`} size="small" />
              </Box>

              {cart.items.map((item, index) => (
                <Box key={item.id}>
                  <Box sx={{ display: 'flex', gap: 2, py: 2 }}>
                    <Box
                      component="img"
                      src={item.image_url || '/placeholder.png'}
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'contain',
                        borderRadius: 1,
                        bgcolor: '#f5f5f5',
                        p: 1
                      }}
                      onError={(e) => { e.target.src = '/placeholder.png'; }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="medium" sx={{ mb: 0.5 }}>
                        {item.name}
                      </Typography>
                      {/* 顯示選擇的規格 */}
                      {item.spec_info && item.spec_info.options && (
                        <Box sx={{ mb: 0.5 }}>
                          {Object.entries(item.spec_info.options).map(([key, value]) => (
                            <Chip
                              key={key}
                              label={`${key}: ${value}`}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        單價: ${(item.display_price || item.sale_price)?.toLocaleString()}
                        {item.spec_info && item.spec_info.optionsPrice > 0 && (
                          <span style={{ color: '#666', fontSize: '0.85em' }}>
                            {' '}(含規格 +${item.spec_info.optionsPrice})
                          </span>
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        數量: {item.quantity}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" color="error" fontWeight="bold">
                        ${item.item_total?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  {index < cart.items.length - 1 && <Divider />}
                </Box>
              ))}
            </Paper>

            {/* 收件資訊 */}
            <Paper id="section-contact" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Avatar sx={{ bgcolor: '#4caf50', width: 32, height: 32 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  收件資訊
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="手機號碼"
                    name="customer_phone"
                    placeholder="09xxxxxxxx"
                    value={form.customer_phone}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    id="field-customer-name"
                    label="收件人姓名"
                    name="customer_name"
                    value={form.customer_name}
                    onChange={handleChange}
                    placeholder="請輸入真實姓名以便取貨"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email (選填)"
                    name="customer_email"
                    type="email"
                    value={form.customer_email}
                    onChange={handleChange}
                    placeholder="接收訂單通知"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* 配送方式 */}
            <Paper id="section-shipping" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Avatar sx={{ bgcolor: '#ff9800', width: 32, height: 32 }}>
                  <StoreIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  配送方式
                </Typography>
              </Box>

              {/* 配送方式選擇 */}
              <RadioGroup
                row
                value={form.shipping_method}
                onChange={(e) => setForm(prev => ({ ...prev, shipping_method: e.target.value }))}
                sx={{ mb: 3 }}
              >
                <FormControlLabel
                  value="cvs"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>🏪</span>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">超商取貨</Typography>
                        <Typography variant="caption" color="text.secondary">7-ELEVEN / 全家</Typography>
                      </Box>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="home"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>🏠</span>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">宅配到府</Typography>
                        <Typography variant="caption" color="text.secondary">送貨到指定地址</Typography>
                      </Box>
                    </Box>
                  }
                />
              </RadioGroup>

              {/* 超商取貨 */}
              {form.shipping_method === 'cvs' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>選擇超商</InputLabel>
                      <Select
                        name="store_type"
                        value={form.store_type}
                        label="選擇超商"
                        onChange={handleChange}
                      >
                        <MenuItem value="7-11">統一超商 7-ELEVEN</MenuItem>
                        <MenuItem value="family">全家便利商店</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleOpenStoreMap}
                      disabled={emapLoading}
                      sx={{ height: '56px', bgcolor: form.store_type === '7-11' ? '#00703c' : '#00a0e9' }}
                      startIcon={emapLoading ? <CircularProgress size={20} color="inherit" /> : <OpenInNewIcon />}
                    >
                      {emapLoading ? '選擇中...' : `開啟${form.store_type === '7-11' ? '7-11' : '全家'}地圖`}
                    </Button>
                  </Grid>

                  {/* 門市選擇提示 */}
                  <Grid item xs={12}>
                    {!form.store_name ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        請點擊上方按鈕開啟地圖選擇門市，選擇後門市資訊將自動帶入
                      </Alert>
                    ) : (
                      <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                        已選擇門市：{form.store_name}
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="門市名稱"
                      name="store_name"
                      value={form.store_name}
                      onChange={handleChange}
                      placeholder="請從地圖選擇門市"
                      required
                      InputProps={{
                        readOnly: !!form.store_name,
                        sx: form.store_name ? { bgcolor: '#f5f5f5' } : {}
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="門市地址"
                      name="store_address"
                      value={form.store_address}
                      onChange={handleChange}
                      placeholder="請從地圖選擇門市"
                      required
                      InputProps={{
                        readOnly: !!form.store_address,
                        sx: form.store_address ? { bgcolor: '#f5f5f5' } : {}
                      }}
                    />
                  </Grid>

                  {/* 已選擇門市資訊 */}
                  {form.store_name && form.store_address && (
                    <Grid item xs={12}>
                      <Alert
                        severity="success"
                        icon={<CheckCircleIcon />}
                        sx={{ borderRadius: 2, mt: 1 }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold">
                          {form.store_type === '7-11' ? '統一超商 7-ELEVEN' : '全家便利商店'} - {form.store_name}
                        </Typography>
                        <Typography variant="body2">{form.store_address}</Typography>
                        <Button
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={() => setForm(prev => ({ ...prev, store_id: '', store_name: '', store_address: '' }))}
                        >
                          重新選擇門市
                        </Button>
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* 宅配到府 */}
              {form.shipping_method === 'home' && (
                <TaiwanAddressSelector
                  city={form.home_city}
                  district={form.home_district}
                  zipcode={form.home_zipcode}
                  address={form.home_address}
                  onCityChange={(val) => setForm(prev => ({ ...prev, home_city: val }))}
                  onDistrictChange={(val) => setForm(prev => ({ ...prev, home_district: val }))}
                  onZipcodeChange={(val) => setForm(prev => ({ ...prev, home_zipcode: val }))}
                  onAddressChange={(val) => setForm(prev => ({ ...prev, home_address: val }))}
                  required
                />
              )}
            </Paper>

            {/* 付款方式 */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Avatar sx={{ bgcolor: '#9c27b0', width: 32, height: 32 }}>
                  <PaymentIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  付款方式
                </Typography>
              </Box>

              {loadingPayments ? (
                <Box>
                  <Skeleton height={70} sx={{ borderRadius: 2 }} />
                  <Skeleton height={70} sx={{ borderRadius: 2, mt: 1 }} />
                </Box>
              ) : (
                <RadioGroup
                  name="payment_method"
                  value={form.payment_method}
                  onChange={handleChange}
                >
                  {paymentMethods.map(method => (
                    <Card
                      key={method.code}
                      sx={{
                        mb: 1,
                        border: form.payment_method === method.code ? '2px solid #1769ff' : '1px solid #eee',
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: '#1769ff' }
                      }}
                      onClick={() => {
                        setForm(prev => ({ ...prev, payment_method: method.code }));
                        // GA4 add_payment_info 事件
                        if (!hasTrackedPayment.current && cart && window.dataLayer) {
                          hasTrackedPayment.current = true;
                          window.dataLayer.push({ ecommerce: null });
                          window.dataLayer.push({
                            event: 'add_payment_info',
                            ecommerce: {
                              currency: 'TWD',
                              value: cart.subtotal,
                              payment_type: method.code,
                              items: cart.items.map(item => ({
                                item_id: item.sku,
                                item_name: item.name,
                                price: item.sale_price,
                                quantity: item.quantity
                              }))
                            }
                          });
                        }
                      }}
                    >
                      <CardContent sx={{ py: 1.5 }}>
                        <FormControlLabel
                          value={method.code}
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              {method.logo_url ? (
                                <img src={method.logo_url} alt={method.name} style={{ height: 24 }} />
                              ) : (
                                <Typography sx={{ fontSize: 24 }}>{method.icon || '💳'}</Typography>
                              )}
                              <Box>
                                <Typography variant="body1" fontWeight="medium">{method.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {method.description}
                                </Typography>
                              </Box>
                            </Box>
                          }
                          sx={{ m: 0, width: '100%' }}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </RadioGroup>
              )}
            </Paper>

            {/* 電子發票 */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <EInvoiceSelector
                value={invoiceData}
                onChange={setInvoiceData}
              />
            </Paper>

            {/* 備註 */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <TextField
                fullWidth
                label="訂單備註 (選填)"
                name="remark"
                multiline
                rows={2}
                value={form.remark}
                onChange={handleChange}
                placeholder="有任何需要備註的事項請填寫"
              />
            </Paper>
          </Grid>

          {/* 右側 - 訂單摘要 */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 2, position: 'sticky', top: 100 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                訂單摘要
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">商品小計</Typography>
                <Typography>${subtotal.toLocaleString()}</Typography>
              </Box>

              {/* 省了多少提示（相對原價的差，非額外折扣） */}
              {totalSavings > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: 'success.main' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalOfferIcon sx={{ fontSize: 18 }} />
                    <Typography color="success.main" sx={{ fontSize: '0.9rem' }}>比原價省</Typography>
                  </Box>
                  <Typography color="success.main" fontWeight="bold">
                    ${Math.round(totalSavings).toLocaleString()}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">運費</Typography>
                <Typography>
                  {shippingFee === 0 ? (
                    <Chip label="免運" size="small" color="success" />
                  ) : (
                    `$${shippingFee}`
                  )}
                </Typography>
              </Box>

              {currentFreeThreshold > 0 && shippingFee > 0 && (
                <Alert severity="info" sx={{ my: 2, borderRadius: 2 }} icon={<LocalOfferIcon />}>
                  再買 ${Math.round(currentFreeThreshold - subtotal).toLocaleString()} 即可免運！
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    滿 ${currentFreeThreshold.toLocaleString()} 免運費
                  </Typography>
                </Alert>
              )}

              {/* 免運達成提示 */}
              {currentFreeThreshold > 0 && shippingFee === 0 && (
                <Alert severity="success" sx={{ my: 2, borderRadius: 2 }}>
                  已達免運門檻，省 ${shippingConfig.base_fee} 運費！
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">總計</Typography>
                <Typography variant="h5" color="error" fontWeight="bold">
                  ${total.toLocaleString()}
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSubmit}
                disabled={loading || loadingPayments}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  bgcolor: '#1769ff',
                  '&:hover': { bgcolor: '#1557cc' },
                  borderRadius: 2
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  getSubmitButtonText()
                )}
              </Button>


              {/* 安全保障 */}
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SecurityIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                  <Typography variant="body2" fontWeight="medium">安全購物保障</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocalShippingIcon sx={{ color: '#1769ff', fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary">2-3 天快速到貨</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalOfferIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary">超值價格</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

    </Box>
  );
};

export default Checkout;
